import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import {
  ArrowRight, Calendar, ChevronUp, Headphones, Home,
  House, LockKeyhole, ShieldCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import StarterPackLeadMagnet from '../components/landing/StarterPackLeadMagnet';
import MaturityDashboardPreview from '../components/landing/MaturityDashboardPreview';
import { ComplianceBento } from '../components/landing/ComplianceBento';
import { ComplianceWorkflow } from '../components/landing/ComplianceWorkflow';
import { ComplianceFAQ } from '../components/landing/ComplianceFAQ';
import { PricingSection } from '../components/landing/PricingSection';

const HOME_URL = 'https://www.aivoracybersol.com/';

const LandingPage = () => {
  const navigate = useNavigate();
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => setShowScrollTop(window.scrollY > 500);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div className="min-h-screen overflow-x-hidden bg-[#07111f] text-slate-100" data-testid="product-landing-page">
      <AnimatePresence>
        {showScrollTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="fixed bottom-5 right-5 z-50 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 bg-[#0b1221]/85 text-white shadow-2xl backdrop-blur-xl transition-colors hover:border-emerald-400/50 hover:bg-emerald-500 hover:text-[#07111f] sm:bottom-8 sm:right-8"
            aria-label="Scroll to top"
            data-testid="landing-scroll-top-button"
          >
            <ChevronUp className="h-5 w-5" />
          </motion.button>
        )}
      </AnimatePresence>

      <nav className="sticky top-0 z-50 border-b border-white/10 bg-[#07111f]/75 backdrop-blur-2xl" data-testid="landing-navigation">
        <div className="mx-auto flex min-h-16 w-full max-w-screen-2xl items-center justify-between gap-3 px-4 py-3 sm:px-6 lg:px-8">
          <button type="button" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })} className="flex min-w-0 items-center gap-3 text-left" data-testid="landing-brand-button">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-emerald-400/30 bg-emerald-400/10">
              <ShieldCheck className="h-5 w-5 text-emerald-300" />
            </span>
            <span className="min-w-0">
              <span className="block truncate text-lg font-semibold text-white">AIVORA Comply360</span>
              <span className="hidden text-[11px] uppercase tracking-[0.18em] text-slate-500 sm:block">Compliance command center</span>
            </span>
          </button>

          <div className="hidden items-center gap-7 lg:flex">
            <a href="#capabilities" className="text-sm text-slate-300 transition-colors hover:text-white" data-testid="nav-capabilities-link">Capabilities</a>
            <a href="#workflow" className="text-sm text-slate-300 transition-colors hover:text-white" data-testid="nav-workflow-link">Workflow</a>
            <a href="#pricing" className="text-sm text-slate-300 transition-colors hover:text-white" data-testid="nav-pricing-link">Pricing</a>
            <a href="#faq" className="text-sm text-slate-300 transition-colors hover:text-white" data-testid="nav-faq-link">FAQ</a>
          </div>

          <div className="flex shrink-0 items-center gap-2">
            <a href={HOME_URL} className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-white/15 bg-white/[0.06] px-3 text-sm text-slate-100 backdrop-blur-xl transition-colors hover:border-emerald-400/40 hover:bg-white/10" data-testid="nav-home-link">
              <Home className="h-4 w-4" /><span className="hidden sm:inline">Home</span>
            </a>
            <Button onClick={() => navigate('/auth')} className="h-10 bg-emerald-500 px-4 text-[#07111f] hover:bg-emerald-400" data-testid="nav-login-button">Log in</Button>
          </div>
        </div>
      </nav>

      <main>
        <section className="relative isolate min-h-[720px] overflow-hidden bg-[#07111f]" data-testid="compliance-hero-section">
          <img src="/images/compliance-command-center.jpeg" alt="A glass compliance command center with control matrices and maturity analytics" className="absolute inset-0 h-full w-full object-cover object-[68%_center]" data-testid="hero-compliance-image" />
          <div className="absolute inset-0 bg-[#07111f]/65" />
          <div className="absolute inset-y-0 left-0 w-full bg-[#07111f]/45 backdrop-blur-[1px] lg:w-[66%]" />
          <motion.div
            className="absolute inset-y-0 w-px bg-emerald-300/50 shadow-[0_0_30px_rgba(52,211,153,0.5)]"
            animate={{ left: ['18%', '88%', '18%'], opacity: [0, 0.65, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: 'easeInOut' }}
            aria-hidden="true"
          />

          <div className="relative mx-auto flex min-h-[720px] w-full max-w-screen-2xl flex-col justify-center px-4 pb-32 pt-20 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.65 }} className="max-w-3xl">
              <div className="mb-7 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-[#07111f]/60 px-4 py-2 text-xs uppercase tracking-[0.2em] text-emerald-200 backdrop-blur-xl" data-testid="hero-standard-badge">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-300 shadow-[0_0_12px_rgba(52,211,153,0.9)]" />
                ISO/IEC 27001:2022 readiness
              </div>
              <h1 className="max-w-3xl text-5xl font-normal leading-[0.96] text-white sm:text-6xl lg:text-7xl" data-testid="hero-title">
                Turn compliance evidence into audit confidence.
              </h1>
              <p className="mt-7 max-w-2xl text-base leading-7 text-slate-200 sm:text-lg" data-testid="hero-description">
                Assess information-security maturity, expose control gaps, manage policies, and prepare decision-ready evidence from one governed workspace.
              </p>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <Button size="lg" onClick={() => navigate('/auth')} className="h-14 bg-emerald-500 px-7 text-base font-semibold text-[#07111f] shadow-[0_18px_45px_rgba(16,185,129,0.22)] hover:bg-emerald-400" data-testid="hero-start-assessment-button">
                  Start assessment <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
                <a href="https://calendly.com/aivora-techlabs/aivora-comply360-isms-maturity-assessment-software" target="_blank" rel="noopener noreferrer" data-testid="hero-book-demo-link">
                  <Button size="lg" variant="outline" className="h-14 w-full border-white/20 bg-[#07111f]/45 px-7 text-base text-white backdrop-blur-xl hover:border-amber-300/60 hover:bg-white/10 hover:text-white sm:w-auto" data-testid="hero-book-demo-button">
                    <Calendar className="mr-2 h-4 w-4 text-amber-300" />Book a demo
                  </Button>
                </a>
                <Button size="lg" variant="ghost" onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })} className="h-14 px-6 text-base text-slate-200 hover:bg-white/10 hover:text-white" data-testid="hero-view-pricing-button">View pricing</Button>
              </div>
              <div className="mt-8 flex flex-wrap gap-x-6 gap-y-3 text-sm text-slate-300" data-testid="hero-trust-points">
                <span className="inline-flex items-center gap-2"><LockKeyhole className="h-4 w-4 text-emerald-300" />Controlled evidence</span>
                <span className="inline-flex items-center gap-2"><House className="h-4 w-4 text-amber-300" />Organization workspaces</span>
                <span className="inline-flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-300" />Audit-ready reporting</span>
              </div>
            </motion.div>
          </div>

          <div className="absolute inset-x-0 bottom-0 border-y border-white/10 bg-[#081526]/80 backdrop-blur-2xl" data-testid="hero-compliance-status-rail">
            <div className="mx-auto grid w-full max-w-screen-2xl grid-cols-2 divide-x divide-white/10 px-4 sm:grid-cols-4 sm:px-6 lg:px-8">
              {[
                ['93', 'Annex A controls', 'text-white'],
                ['4', 'Control domains', 'text-white'],
                ['PASS', 'Mature controls', 'text-emerald-300'],
                ['RISK', 'Priority findings', 'text-red-300'],
              ].map(([value, label, color]) => (
                <div key={label} className="px-4 py-4 sm:py-5" data-testid={`hero-metric-${label.toLowerCase().replaceAll(' ', '-')}`}>
                  <p className={`text-xl font-semibold ${color}`}>{value}</p><p className="mt-1 text-[11px] uppercase tracking-[0.16em] text-slate-500">{label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <ComplianceBento />
        <ComplianceWorkflow />
        <MaturityDashboardPreview />
        <StarterPackLeadMagnet />
        <PricingSection />
        <ComplianceFAQ />
      </main>

      <footer className="border-t border-white/10 bg-[#050b14] py-10" data-testid="landing-footer">
        <div className="mx-auto flex w-full max-w-screen-2xl flex-col justify-between gap-8 px-4 sm:px-6 md:flex-row md:items-center lg:px-8">
          <div className="flex items-center gap-3"><ShieldCheck className="h-5 w-5 text-emerald-300" /><div><p className="font-semibold text-white">AIVORA Comply360</p><p className="mt-1 text-xs text-slate-500">Designed and owned by AIVORA Techlabs Global Pvt Ltd</p></div></div>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3 text-sm">
            <a href={HOME_URL} className="text-slate-400 transition-colors hover:text-white" data-testid="footer-home-link">AIVORA CyberSol</a>
            <a href="/terms" className="text-slate-400 transition-colors hover:text-white" data-testid="footer-terms-link">Terms</a>
            <a href="/privacy" className="text-slate-400 transition-colors hover:text-white" data-testid="footer-privacy-link">Privacy</a>
            <a href="mailto:support@aivora-techlabs.com" className="inline-flex items-center gap-2 text-emerald-300 transition-colors hover:text-emerald-200" data-testid="footer-support-link"><Headphones className="h-4 w-4" />Support</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;